import React, { useContext, useEffect, useState } from 'react';

import cogTokens from '@amzn/meridian-tokens/base/icon/cog';
import refreshTokens from '@amzn/meridian-tokens/base/icon/refresh';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Pagination from '@amzn/meridian/pagination';
import Row from '@amzn/meridian/row';
import SearchField from '@amzn/meridian/search-field';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import Tooltip from '@amzn/meridian/tooltip';

import RCTooltip from 'rc-tooltip';
import 'rc-tooltip/assets/bootstrap.css';
import { useHistory } from 'react-router-dom';
import { EmbeddedScrollable } from 'src/components/EmbeddedScrollable/EmbeddedScrollable';
import { WRITER_ROLE_SET } from 'src/constants/role';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';

import { Conversation } from '../types';
import { filterConversationsByUser, groupConversationsByDate } from '../utils';
import './AIChatList.scss';

interface AIChatListProps {
    cachedConversations?: Conversation[];
    isLoadingConversations?: boolean;
    currentConversationId?: string | null;
    onRefresh?: () => void;
}

export const AIChatList: React.FC<AIChatListProps> = ({
    cachedConversations = [],
    isLoadingConversations = false,
    currentConversationId = null,
    onRefresh
}) => {
    const [showAllConversations, setShowAllConversations] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const history = useHistory();
    const { userInfo, userRole } = useContext(AppContext);
    const CONVERSATIONS_PER_PAGE = 25;

    const AI_PILOT_GROUP = ['haggarta', 'ashmhan', 'hxinzhuo', 'wufann', 'igaetan', 'rampatat', 'ushma', 'rileyhh'];

    const getFilteredConversations = (): Conversation[] => {
        let conversations = showAllConversations
            ? cachedConversations
            : filterConversationsByUser(cachedConversations, userInfo.username);

        if (searchQuery.trim()) {
            conversations = conversations.filter(
                (conversation) =>
                    conversation.firstMessage?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    conversation.user.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        return conversations;
    };

    const filteredConversations = getFilteredConversations();
    const totalPages = Math.ceil(filteredConversations.length / CONVERSATIONS_PER_PAGE);
    const startIndex = (currentPage - 1) * CONVERSATIONS_PER_PAGE;
    const endIndex = startIndex + CONVERSATIONS_PER_PAGE;
    const paginatedConversations = filteredConversations.slice(startIndex, endIndex);
    const paginatedGroupedConversations = groupConversationsByDate(paginatedConversations);

    // Reset to first page when search query changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, showAllConversations]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleNewChat = () => {
        history.push(ROUTES.AI.INDEX);
    };

    const handleSelectConversation = (conversationId: string) => {
        history.push(`${ROUTES.AI.INDEX}/${conversationId}`);
    };

    const handleRefresh = () => {
        // Reset filters
        setSearchQuery('');
        setCurrentPage(1);

        // Call the refresh function from parent
        if (onRefresh) {
            onRefresh();
        }
    };
    return (
        <Column height={'100%'} heights={['fit', 'fill']} className="ai-chat-list">
            <Column spacing="100" className="chat-list-header">
                <Row widths={['fill', 'fit']} spacing={'100'}>
                    <Button type="primary" size="medium" onClick={handleNewChat} disabled={isLoadingConversations}>
                        New Chat
                    </Button>
                    <Tooltip position={'end'} title={'Configure Chat'}>
                        <Button type="link" onClick={() => history.push(ROUTES.SETTINGS.AI)} className="config-button">
                            <Icon tokens={cogTokens} />
                        </Button>
                    </Tooltip>
                </Row>
                {cachedConversations.length > 0 && (
                    <>
                        <Row alignmentVertical="center" spacing="200" alignmentHorizontal={'justify'}>
                            <Row alignmentVertical="center" spacing="200">
                                <Text type="b400">Chat History</Text>
                                {onRefresh && (
                                    <Tooltip position={'end'} title={'Refresh History'}>
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={handleRefresh}
                                            disabled={isLoadingConversations}
                                        >
                                            <Icon tokens={refreshTokens} />
                                        </Button>
                                    </Tooltip>
                                )}
                            </Row>
                            {WRITER_ROLE_SET.has(userRole) && AI_PILOT_GROUP.includes(userInfo.username) && (
                                <Row alignmentVertical="center" spacing="200" className="show-all-toggle">
                                    <Text type="b200">Show All</Text>
                                    <Toggle
                                        checked={showAllConversations}
                                        onChange={setShowAllConversations}
                                        size="small"
                                    />
                                </Row>
                            )}
                        </Row>
                        <Column spacing="200" spacingInset="100">
                            <SearchField
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder="Search by message or user..."
                                onSubmit={() => {}}
                                searchButton={true}
                                clearButton={true}
                                size="small"
                            />
                            {searchQuery.trim() && (
                                <Text type="b200" color="secondary">
                                    Showing {filteredConversations.length} of{' '}
                                    {showAllConversations
                                        ? cachedConversations.length
                                        : filterConversationsByUser(cachedConversations, userInfo.username).length}{' '}
                                    chats
                                </Text>
                            )}
                        </Column>
                    </>
                )}
            </Column>
            {isLoadingConversations ? (
                <Column width="100%" height="100%" alignmentVertical="center">
                    <Loader size="small" />
                    <Text>Loading chat history</Text>
                </Column>
            ) : cachedConversations.length === 0 ? (
                <Column width="100%" height="100%" alignmentVertical="center">
                    <Text>No chat yet</Text>
                </Column>
            ) : (
                <Column heights={['fill', 'fit']} spacing="100" alignmentVertical="justify">
                    <EmbeddedScrollable>
                        <Column className="chat-list-scrollable">
                            {filteredConversations.length === 0 ? (
                                <Text color="secondary">
                                    {searchQuery.trim() ? 'No chats match your search' : 'No chats found'}
                                </Text>
                            ) : (
                                Object.entries(paginatedGroupedConversations).map(([dateGroup, conversations]) => (
                                    <Column key={dateGroup} spacing="100">
                                        <Text type="b300" color="secondary">
                                            {dateGroup}
                                        </Text>
                                        <Column spacing="100">
                                            {conversations.map((conversation) => (
                                                <ConversationItem
                                                    key={conversation.conversationId}
                                                    conversation={conversation}
                                                    onSelect={() =>
                                                        handleSelectConversation(conversation.conversationId)
                                                    }
                                                    showUsername={showAllConversations}
                                                    isSelected={conversation.conversationId === currentConversationId}
                                                />
                                            ))}
                                        </Column>
                                    </Column>
                                ))
                            )}
                        </Column>
                    </EmbeddedScrollable>
                    {totalPages > 1 && (
                        <Row alignmentHorizontal="center" className="chat-list-pagination">
                            <Pagination
                                currentPage={currentPage}
                                numberOfPages={totalPages}
                                onChange={handlePageChange}
                            />
                        </Row>
                    )}
                </Column>
            )}
        </Column>
    );
};

interface ConversationItemProps {
    conversation: Conversation;
    onSelect: () => void;
    showUsername: boolean;
    isSelected: boolean;
}

const ConversationItem: React.FC<ConversationItemProps> = ({ conversation, onSelect, showUsername, isSelected }) => {
    const message = conversation.firstMessage || 'Chat';
    const truncatedMessage = message.length > 200 ? message.slice(0, 200) + '...' : message;

    return (
        <RCTooltip
            overlay={
                <div style={{ maxWidth: '280px', wordWrap: 'break-word', fontSize: '13px' }}>{truncatedMessage}</div>
            }
            placement="right"
            overlayClassName="conversation-rc-tooltip"
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.05}
        >
            <Button
                type="link"
                onClick={onSelect}
                className={`conversation-item-button ${isSelected ? 'selected' : ''}`}
            >
                <Column spacing="100" width="100%">
                    <Row spacing={'none'} className="conversation-content">
                        <div style={{ visibility: isSelected ? 'visible' : 'hidden' }}>
                            <Tag />
                        </div>
                        <Text type="b300" className="conversation-message-text">
                            {conversation.firstMessage || 'Chat'}
                        </Text>
                    </Row>
                    <Row alignmentHorizontal="end" alignmentVertical="center">
                        {showUsername && (
                            <Text type="b100" color="secondary">
                                @{conversation.user}
                            </Text>
                        )}
                    </Row>
                </Column>
            </Button>
        </RCTooltip>
    );
};
